const normalize = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const ROUND_COUNT = 25; // rounds 0..24

// Display order for per-source series: internal counts, then external sources.
export const DATASET_SOURCES = [
  { key: 'count1', label: 'BG 1', field: 'game_vote_data_count_1' },
  { key: 'count2', label: 'BG 2', field: 'game_vote_data_count_2' },
  { key: 'count3', label: 'BG 3', field: 'game_vote_data_count_3' },
  { key: 'count4', label: 'BG 4', field: 'game_vote_data_count_4' },
  { key: 'count5', label: 'BG 5', field: 'game_vote_data_count_5' },
  { key: 'betfair', label: 'Betfair', field: 'game_vote_data_betfair' },
  { key: 'espn', label: 'ESPN', field: 'game_vote_data_espn' },
  { key: 'byb', label: 'BYB', field: 'game_vote_data_byb' },
];

/**
 * Per-round vote totals for a single player, averaged across the enabled
 * datasets and keyed by round string. Shared by the modal's round breakdown
 * and progression charts so both read from an identical pipeline.
 */
export const getPlayerVotesByRound = (player, gameVoteData, enabledKeys = []) => {
  const playerVotes = {};

  if (!player || !gameVoteData) {
    return playerVotes;
  }

  const normalizedPlayerName = normalize(player.name);
  const normalizedPlayerTeam = normalize(player.full_team_name || player.team);

  // Build list of per-source datasets from enabled keys
  const datasets = [];
  const pushIf = (key, data) => { if (enabledKeys.includes(key) && data) datasets.push(Array.isArray(data) ? { game_vote_data: data } : data); };
  pushIf('byb', gameVoteData.game_vote_data_byb);
  pushIf('espn', gameVoteData.game_vote_data_espn);
  pushIf('betfair', gameVoteData.game_vote_data_betfair);
  pushIf('count1', gameVoteData.game_vote_data_count_1);
  pushIf('count2', gameVoteData.game_vote_data_count_2);
  pushIf('count3', gameVoteData.game_vote_data_count_3);
  pushIf('count4', gameVoteData.game_vote_data_count_4);
  pushIf('count5', gameVoteData.game_vote_data_count_5);

  // Fallback to combined dataset if no enabled per-source provided
  if (datasets.length === 0 && gameVoteData.game_vote_data) {
    datasets.push({ game_vote_data: gameVoteData.game_vote_data });
  }

  const denom = datasets.length || 1;

  datasets.forEach((ds) => {
    (ds.game_vote_data || []).forEach((roundData) => {
      const round = roundData.round;
      let roundTotal = 0;
      roundData.game_votes.forEach((game) => {
        const playerVote = game.votes.find((vote) => {
          const nameMatch = normalize(vote.player) === normalizedPlayerName;
          const teamMatch = normalize(vote.team) === normalizedPlayerTeam;
          return nameMatch && teamMatch;
        });
        if (playerVote) roundTotal += Number(playerVote.votes) || 0;
      });
      if (!playerVotes[round]) playerVotes[round] = 0;
      playerVotes[round] += roundTotal;
    });
  });

  // Average across enabled datasets
  Object.keys(playerVotes).forEach((r) => {
    playerVotes[r] = playerVotes[r] / denom;
  });

  return playerVotes;
};

/**
 * Per-round vote totals for a single player, kept split by source instead of
 * averaged: one entry per enabled dataset, each with a dense rounds[0..24]
 * array. Averaging these per round reproduces getPlayerVotesByRound, so the
 * consensus line and the round breakdown always agree.
 */
export const getPlayerRoundsByDataset = (player, gameVoteData, enabledKeys = []) => {
  if (!player || !gameVoteData) return [];

  const playerName = normalize(player.name);
  const playerTeam = normalize(player.full_team_name || player.team);

  const extract = (raw) => {
    const dataset = Array.isArray(raw) ? { game_vote_data: raw } : raw;
    const rounds = new Array(ROUND_COUNT).fill(0);
    (dataset?.game_vote_data || []).forEach((roundData) => {
      const round = Number(roundData.round);
      if (!Number.isInteger(round) || round < 0 || round >= ROUND_COUNT) return;
      let roundTotal = 0;
      (roundData.game_votes || []).forEach((game) => {
        const playerVote = (game.votes || []).find(
          (vote) => normalize(vote.player) === playerName && normalize(vote.team) === playerTeam
        );
        if (playerVote) roundTotal += Number(playerVote.votes) || 0;
      });
      rounds[round] += roundTotal;
    });
    return rounds;
  };

  const series = [];
  DATASET_SOURCES.forEach(({ key, label, field }) => {
    if (!enabledKeys.includes(key)) return;
    const raw = gameVoteData[field];
    if (!raw) return;
    series.push({ key, label, rounds: extract(raw) });
  });

  // Mirror the averaged helper's fallback to the combined dataset.
  if (series.length === 0 && gameVoteData.game_vote_data) {
    series.push({ key: 'combined', label: 'All', rounds: extract(gameVoteData.game_vote_data) });
  }

  return series;
};

export default getPlayerVotesByRound;
